import { useEffect, useState } from 'react';
import { SecurityManager } from '../security/security-manager';
import { UserService } from '../service/user.service';
import { UserRes } from '../response/user.response';

export const NotFound = () => {
  const [user, setUser] = useState<UserRes & { domainUrl: string }>({} as any);

  const fetchUser = async () => {
    const uid = SecurityManager.getUid();
    const [user, err] = await UserService.getUserTestFeedback(uid);
    if (err) {
      return;
    }
    setUser(user);
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleGoHome = () => {
    if (user.domainUrl) {
      window.location.href = user.domainUrl;
    } else {
      window.location.href = '/';
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Montserrat, sans-serif',
    background:
      'linear-gradient(135deg, #002A52 0%, #0a1628 50%, #001a33 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '2rem',
  };

  const floatingShapeStyle = (
    delay: number,
    size: number,
    left: string,
    top: string,
  ): React.CSSProperties => ({
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background:
      'linear-gradient(135deg, rgba(46, 129, 247, 0.15), rgba(12, 189, 128, 0.1))',
    animation: `float 6s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    left,
    top,
    filter: 'blur(1px)',
  });

  const errorCodeStyle: React.CSSProperties = {
    fontSize: 'clamp(8rem, 20vw, 14rem)',
    fontWeight: 800,
    background:
      'linear-gradient(135deg, #2E81F7 0%, #0CBD80 50%, #2E81F7 100%)',
    backgroundSize: '200% 200%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: 'gradientShift 4s ease infinite',
    textShadow: '0 0 80px rgba(46, 129, 247, 0.3)',
    letterSpacing: '-0.05em',
    lineHeight: 1,
    margin: 0,
    position: 'relative',
    zIndex: 2,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: 'clamp(1rem, 3vw, 1.5rem)',
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: '-0.5rem',
    marginBottom: '1.5rem',
    fontWeight: 300,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    position: 'relative',
    zIndex: 2,
  };

  const messageStyle: React.CSSProperties = {
    fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
    maxWidth: '480px',
    lineHeight: 1.7,
    marginBottom: '2.5rem',
    position: 'relative',
    zIndex: 2,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '1rem 2.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    color: '#ffffff',
    background: 'linear-gradient(135deg, #2E81F7 0%, #0CBD80 100%)',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 32px rgba(46, 129, 247, 0.3)',
    position: 'relative',
    zIndex: 2,
    textDecoration: 'none',
  };

  const decorativeLineStyle: React.CSSProperties = {
    width: '120px',
    height: '4px',
    background:
      'linear-gradient(90deg, transparent, #2E81F7, #0CBD80, transparent)',
    borderRadius: '2px',
    margin: '1.5rem 0',
    position: 'relative',
    zIndex: 2,
  };

  const gridPatternStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(46, 129, 247, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(46, 129, 247, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px',
    pointerEvents: 'none',
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
            50% { transform: translateY(-30px) rotate(180deg); opacity: 1; }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .not-found-btn:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 40px rgba(46, 129, 247, 0.45) !important;
          }
        `}
      </style>

      <div style={gridPatternStyle} />

      <div style={floatingShapeStyle(0, 200, '10%', '20%')} />
      <div style={floatingShapeStyle(1, 150, '80%', '15%')} />
      <div style={floatingShapeStyle(2, 100, '75%', '70%')} />
      <div style={floatingShapeStyle(3, 180, '5%', '75%')} />
      <div style={floatingShapeStyle(1.5, 80, '50%', '85%')} />
      <div style={floatingShapeStyle(2.5, 120, '90%', '50%')} />

      <h1 style={errorCodeStyle}>404</h1>
      <p style={subtitleStyle}>Page Not Found</p>
      <div style={decorativeLineStyle} />
      <p style={messageStyle}>
        The page you're looking for seems to have wandered off into the digital
        void. Let's get you back on track.
      </p>
      <button
        className="not-found-btn"
        style={buttonStyle}
        onClick={handleGoHome}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        Return Home
      </button>
    </div>
  );
};
